if (!customElements.get('ap-sticky-atc')) {
  customElements.define(
    'ap-sticky-atc',
    class ApStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.nativeButton = document.getElementById(this.dataset.nativeButton);
        this.priceRoot = document.getElementById(this.dataset.priceRoot);
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.labelEl = this.querySelector('[data-sticky-label]');
        this.footer = document.querySelector(this.dataset.footerSelector);
        this.drawer = document.querySelector(this.dataset.drawerSelector);
        this.media = window.matchMedia('(max-width: 749px)');
        this.nativePassed = false;
        this.footerVisible = false;
        this.addToCartLabel = this.labelEl?.textContent?.trim() || 'Add to cart';

        if (!this.nativeButton || !this.submitButton) return;

        this.submitButton.addEventListener('click', () => {
          if (this.submitButton.disabled) return;
          this.nativeButton.click();
        });

        this.media.addEventListener('change', () => this.refreshNativePosition());
        window.addEventListener('resize', () => this.refreshNativePosition(), { passive: true });
        this.syncFromNative();
        this.observeNative();
        this.observeDrawer();
        this.refreshNativePosition();
      }

      observeNative() {
        this.observers = [];

        if ('IntersectionObserver' in window) {
          const nativeObserver = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              const rect = entry.boundingClientRect;
              this.nativePassed = rect.bottom < 0;
              this.updateVisibility();
            },
            { threshold: [0, 0.01, 0.2, 1] }
          );
          nativeObserver.observe(this.nativeButton);
          this.observers.push(nativeObserver);

          if (this.footer) {
            const footerObserver = new IntersectionObserver(
              (entries) => {
                this.footerVisible = entries.some((entry) => entry.isIntersecting);
                this.updateVisibility();
              },
              { threshold: [0, 0.05] }
            );
            footerObserver.observe(this.footer);
            this.observers.push(footerObserver);
          }
        }

        this.mutationObserver = new MutationObserver(() => this.syncFromNative());
        this.mutationObserver.observe(this.nativeButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });
        if (this.priceRoot) {
          this.mutationObserver.observe(this.priceRoot, { childList: true, subtree: true, characterData: true });
        }
      }

      observeDrawer() {
        if (!this.drawer || !window.MutationObserver) return;
        this.drawerObserver = new MutationObserver(() => this.updateVisibility());
        this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class', 'open'] });
      }

      drawerOpen() {
        if (!this.drawer) return false;
        return this.drawer.classList.contains('active') || this.drawer.hasAttribute('open');
      }

      syncFromNative() {
        const disabled =
          this.nativeButton.hasAttribute('disabled') || this.nativeButton.getAttribute('aria-disabled') === 'true';
        const nativeLabel = this.nativeButton.querySelector(':scope > span');
        const label = (nativeLabel?.textContent || this.addToCartLabel).replace(/\s+/g, ' ').trim();

        this.submitButton.disabled = disabled;
        this.submitButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        this.submitButton.classList.toggle('is-sold-out', disabled);
        if (this.labelEl && label) this.labelEl.textContent = label;
        this.submitButton.classList.toggle('loading', this.nativeButton.classList.contains('loading'));

        if (this.priceEl && this.priceRoot) {
          const sale = this.priceRoot.querySelector('.price--on-sale .price-item--sale');
          const last = this.priceRoot.querySelector('.price-item--last');
          const regular = this.priceRoot.querySelector('.price-item--regular');
          const source = sale || last || regular;
          const price = (source?.textContent || '').replace(/\s+/g, ' ').trim();
          if (price) this.priceEl.textContent = price;
        }
      }

      refreshNativePosition() {
        if (!this.nativeButton) return;
        this.nativePassed = this.nativeButton.getBoundingClientRect().bottom < 0;
        this.updateVisibility();
      }

      updateVisibility() {
        const show =
          this.media.matches && this.nativePassed && !this.footerVisible && !this.drawerOpen();

        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.documentElement.classList.toggle('ap-sticky-atc-on', show);
      }

      disconnectedCallback() {
        this.observers?.forEach((observer) => observer.disconnect());
        this.mutationObserver?.disconnect();
        this.drawerObserver?.disconnect();
        document.documentElement.classList.remove('ap-sticky-atc-on');
      }
    }
  );
}
